import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Card, Col, Button, Row, Alert } from "antd";
import firebase from "firebase/app";
import { EyeInvisibleOutlined, EyeTwoTone, CheckCircleOutlined, InstagramOutlined, MailOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import { REGISTER_USER_REQUEST, CHNAGE_LANGUAGE } from "../../redux/constants";
import { getAuthState, getAppState } from "../../redux/selectors";
import { ContentRow, FormCol, FormInput, FormButton, NavigateBar, NavigateButton, FormPassword } from "../../components/GlobalStyles";
import Logo from "../../assets/images/setpoint-logo.png";

export function Register(): ReactElement {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { currentUser, emailVerified } = useSelector(getAuthState);
  const { locale } = useSelector(getAppState);

  const [fullName, setFullName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [gender, setGender] = useState<string>("male");
  const [error, setError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");


  const goLogin = () => {
    history.push("/login");
  };

  const signupFn = () => {
    if (fullName === "" || email === "" || password === "" || phoneNumber === "") {
      setError(true);
      setErrorMsg("All fields are required!");
    } else {
      firebase
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(() => {
          const currentUserFirebase = firebase.auth().currentUser;
          currentUserFirebase?.sendEmailVerification()
            .then(() => {
              dispatch({
                type: REGISTER_USER_REQUEST,
                payload: {
                  fullName,
                  gender,
                  email,
                  password,
                  phoneNumber,
                  user: currentUserFirebase,
                },
              });
              history.push("/email_verify");
            })
            .catch((e) => {
              setError(true);
              setErrorMsg(e.message);
            });
        })
        .catch((err) => {
          setError(true);
          setErrorMsg(err.message);
        });
    }
  };

  useEffect(() => {
    if (currentUser && emailVerified) {
      history.push("/booking");
    }
  }, [currentUser, emailVerified]);

  const onChangeGender = (value: string) => {
    setGender(value);
  };

  const onChangeLanguage = (code: string) => {
    dispatch({
      type: CHNAGE_LANGUAGE,
      payload: code
    })
    i18n.changeLanguage(code);
  }

  return (
    <ContentRow className="auth-container">
      <div className="landing-language-select-container">
        <Button className="language-selector" onClick={() => onChangeLanguage(locale === "en" ? "ar" : "en")}>{t("common.language")}</Button>
      </div>
      <Col style={{ maxWidth: 360 }}>
        <Card className="auth-form">
          <FormCol span={24}>
            <img src={Logo} alt="Logo" className="auth-logo" />
            <h4 className="setpoint-slogan">{t("auth.find_your_match")}</h4>
            <FormInput
              placeholder={t("auth.fullName")}
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              onPressEnter={signupFn}
            />
            <PhoneInput country="ae" value={phoneNumber}
              onlyCountries={["eg", "bh", "qa", "sa", "kw", "ae"]}
              onChange={(phone) => setPhoneNumber(phone)}
              containerStyle={{ marginTop: 10 }}
              inputStyle={{ color: "#fff" }}
              buttonStyle={{ backgroundColor: "transparent", border: "none" }}
            />
            <Row style={{ width: "100%", marginTop: 10 }}>
              <Col span={12} style={{ paddingRight: 5 }}>
                <Button
                  block
                  type={gender === "male" ? "primary" : "default"}
                  style={{ height: 40, borderRadius: 5 }}
                  icon={gender === "male" ? <CheckCircleOutlined /> : null}
                  onClick={() => onChangeGender("male")}
                >
                  {t("profile.male")}
                </Button>
              </Col>
              <Col span={12} style={{ paddingLeft: 5 }}>
                <Button
                  block
                  type={gender === "female" ? "primary" : "default"}
                  style={{ height: 40, borderRadius: 5 }}
                  icon={gender === "female" ? <CheckCircleOutlined /> : null}
                  onClick={() => onChangeGender("female")}
                >
                  {t("profile.female")}
                </Button>
              </Col>
            </Row>
            <FormInput placeholder={t("auth.email")} value={email} onChange={(event) => setEmail(event.target.value)} onPressEnter={signupFn} />
            <FormPassword
              placeholder={t("auth.password")}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              iconRender={(visible: any) =>
                visible ? <EyeTwoTone style={{ color: "#fff" }} /> : <EyeInvisibleOutlined style={{ color: "#fff" }} />
              }
              className="form-password"
              onPressEnter={signupFn}
            />
            <FormButton type="primary" block onClick={signupFn}>
              {t("auth.register")}
            </FormButton>
            {error && <Alert message={errorMsg} type="error" style={{ marginTop: 10, width: "100%" }} />}
            <NavigateBar>
              <span style={{ color: "#fff" }}>{t("auth.yesAccount")}</span>
              <NavigateButton type="link" onClick={goLogin}>
                {t("auth.go_to_login")}
              </NavigateButton>
            </NavigateBar>
          </FormCol>
        </Card>
      </Col>
      <div className="contact-us-container">
        {/* <span>{t("menu.contact_us")}: </span>
        <a href="mailto:hi@setpointapp.com" target="_blank" rel="noreferrer">hi@setpointapp.com</a> */}
        <Button href="mailto:hi@setpointapp.com" type="link" icon={<MailOutlined />} target="_blank">hi@setpointapp.com</Button>
        <Button href="https://www.instagram.com/setpointapp/" type="link" icon={<InstagramOutlined />} target="_blank">Instagram</Button>
      </div>
    </ContentRow>
  );
}
